import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useInfiniteQuery } from "@tanstack/react-query";
import type {
  ChatFilterMode,
  ChatMessage,
  ChatUser,
  ChatUserMap,
} from "../types/chatMessageTypes";

interface ChatMessagesResponse {
  messages: ChatMessage[];
  hasMore: boolean;
  beforeCreated: string;
  users?: ChatUser[];
}

interface UseChatMessagesInfiniteParams {
  roomToken?: string;
  filterMode: ChatFilterMode;
  enabled?: boolean;
}

/**
 * Mapping from ChatFilterMode to API mode parameter
 */
const FILTER_MODE_TO_API_MODE: Record<ChatFilterMode, string> = {
  combined: "all",
  live: "live",
  async: "async",
};

/**
 * Hook for fetching chat messages with infinite scrolling/pagination
 * @param roomToken - The room token for the chat
 * @param filterMode - The filter mode to apply (live, async, or combined)
 */
export const useChatMessagesInfinite = ({
  roomToken,
  filterMode,
  enabled = true,
}: UseChatMessagesInfiniteParams) => {
  return useInfiniteQuery<
    ChatMessagesResponse,
    Error,
    {
      pages: ChatMessagesResponse[];
      messages: ChatMessage[];
      userMap: ChatUserMap;
    }
  >({
    queryKey: QueryKeys.Patient.Chat.Messages.DEFAULT(roomToken, filterMode),
    queryFn: async ({ pageParam }) => {
      const params: {
        mode: string;
        roomToken?: string;
        beforeCreated?: string | undefined;
        [key: string]: string | undefined;
      } = {
        mode: FILTER_MODE_TO_API_MODE[filterMode],
        roomToken,
      };

      // Add the beforeCreated parameter for pagination if it exists
      if (pageParam) {
        params.beforeCreated = pageParam as string;
      }

      const response = await axiosInstance.get<ChatMessagesResponse>(
        "new-api/chat/messages",
        {
          params,
        },
      );

      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      // Check if there are more messages to load
      if (!lastPage.hasMore) {
        return undefined;
      }

      // Return the beforeCreated value for pagination
      return lastPage.beforeCreated;
    },
    select: (data) => {
      // Reverse the pages array so older messages (from newer pages) appear first
      const reversedPages = [...data.pages].reverse();

      // Flatten all messages from all pages
      const allMessages = reversedPages.flatMap((page) => page.messages);

      // Create a map of users from all pages
      const userMap: ChatUserMap = {};
      for (const page of data.pages) {
        if (page.users) {
          for (const user of page.users) {
            userMap[user.userId.toString()] = user;
          }
        }
      }

      return {
        pages: data.pages, // Keep the original pages for pagination
        messages: allMessages, // Add the processed messages
        userMap, // Add the user map
      };
    },
    enabled: Boolean(roomToken) && enabled,
    staleTime: 0, // Always consider data stale to force refetch on mount
    refetchOnMount: "always", // Always refetch when component mounts
  });
};
